import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import {BaseComponent} from "../../base.component";
import {MarkupService} from "../../common/markup.service";
import {LoggerService} from "../../common/logger.service";
import {Constants} from "../../constants";
import {UserService} from "../../common/user.service";
import {Util} from "../../util/util";
import {GqlCartProductService} from "../../product/graphql/gql-cart-product.service";
import {IProduct} from "../../product/product-models";
import _ from "lodash";
import {CartService} from "../../cart/cart.service";
import {concatMap, map, switchMap, tap} from "rxjs/operators";
import {from, Observable, of, Subscription} from "rxjs";
import {SkinAnalyzerService} from "../skin-analyzer.service";
import {IShopUser} from "../../session/customer-models";
import {ShareListService} from "../../social/share-list.service";
import {IShareList} from "../../social/social-models";
import {BsModalService} from "ngx-bootstrap/modal";
import {CreateShareListModalComponent} from "../../social/create-share-list-modal/create-share-list-modal.component";
import {GeneralErrorPopupComponent} from "../../validation/general-error-popup.component";
import {ProductUtil} from "../../util/product-util";
import {GqlShareListService} from "../../social/share-list/gql-share-list.service";

@Component({
    selector: 'analyzer-results',
    templateUrl: './analyzer-results.component.html',
    styleUrls: ['./analyzer-results.component.scss'],
    standalone: false
})
export class AnalyzerResultsComponent extends BaseComponent implements OnInit, OnDestroy {

    result: any;
    busy: boolean = false;
    isProductAdded: boolean = false;
    productCards: any[] = [];

    subscriptions: any = new Subscription();

    constructor(
        private userService: UserService,
        loggerService: LoggerService,
        private markupService: MarkupService,
        private router: Router,
        private gqlCartProductService: GqlCartProductService,
        private cartService: CartService,
        private skinAnalyzerService: SkinAnalyzerService,
        private gqlShareListService: GqlShareListService,
        private shareListService: ShareListService,
        private modalService: BsModalService
    ) {
        super(userService, loggerService);
    }


    ngOnInit() {
        this.markupService.setTitleFromTranslationTag("cart.skin.analyzer.recommendations.page.title");
        this.initSkinConfigLoader();
        this.processSkinAnalyzerResult();
    }

    processSkinAnalyzerResult() {
        const skinAnalyzerResults: any = this.getSavedResults();
        if (!!skinAnalyzerResults?.usanaIds && skinAnalyzerResults?.usanaIds.length > 0) {
            this.subscriptions.add(
                this.getProductCards(skinAnalyzerResults?.usanaIds).pipe(
                    tap(productCards => this.productCards = productCards),
                    map(productCards => this.skinAnalyzerService.getFilteredProducts(productCards)),
                    switchMap(filteredUsanaIds =>
                        this.createNewShareList(filteredUsanaIds).pipe(
                            switchMap(shareListId => {
                                //on refresh, we don't want to send the email again
                                const emailProcessed: any = Util.parseBoolean(sessionStorage.getItem(Constants.SKIN_ANALYZER_EMAIL_PROCESSED));
                                if (!!skinAnalyzerResults?.email && !emailProcessed) {
                                    sessionStorage.setItem(Constants.SKIN_ANALYZER_EMAIL_PROCESSED, "true");
                                    return this.skinAnalyzerService.sendSkinAnalyzerEmail(
                                        skinAnalyzerResults,
                                        filteredUsanaIds,
                                        shareListId
                                    );
                                }
                                return of(null);
                            })
                        )
                    )
                ).subscribe()
            );
        }
    }

    getSavedResults(): any {
        const skinAnalyzerResults: any = Util.parseJson(sessionStorage.getItem(Constants.SKIN_ANALYZER_RESULTS_INFO));
        if (!skinAnalyzerResults?.usanaIds || skinAnalyzerResults?.usanaIds.length === 0) {
            this.log.warn("No recommended products found for skin analyzer results");
            return {};
        } else {
            return skinAnalyzerResults;
        }
    }

    initSkinConfigLoader() {
        this.subscriptions.add(
            this.userService.getShopUser().pipe(
                map(shopUser => {
                    const revieveConfig = this.getRevieveConfig(shopUser);
                    this.skinAnalyzerService.loadAndConfigureAnalyzer(revieveConfig);
                })
            ).subscribe()
        );
    }

    ngOnDestroy(): void {
        this.log.debug("Destroy");
        this.subscriptions.unsubscribe();
    }

    getRevieveConfig(shopUser: IShopUser): any {
        const revieveConfig: any = this.skinAnalyzerService.getBaseRevieveConfig(shopUser);
        revieveConfig.onDisplayProducts = (_historyId, _products, _sessionId) => {
            window['Revieve'].API.getAdvisorData((userData) => {
                this.log.debug("Revieve user data: ", userData); // Use this data to display product recommendations to the user
                window['Revieve'].API.navigateBack();
                this.skinAnalyzerService.setupSkinAnalyzerResults(userData);
                this.processSkinAnalyzerResult();
            });
        };
        return revieveConfig;
    }

    createNewShareList(usanaIds: string[]): Observable<number> {
        const shareListId = sessionStorage.getItem(Constants.SKIN_ANALYZER_SHARE_LIST_ID);
        if (shareListId) {
            return of(Util.parseInt(shareListId));
        }

        return this.shareListService.createNewList(usanaIds, true, true).pipe(
            map((shareList: IShareList) => {
                sessionStorage.setItem((Constants.SKIN_ANALYZER_SHARE_LIST_ID), shareList.shareListId.toString());
                return shareList.shareListId;
            })
        );
    }

    getProductCards(usanaIds: string[]): Observable<any[]> {
        this.busy = true;
        return this.gqlCartProductService.getProductGroupsWithMap(usanaIds).pipe(
            map(productGroups => {
                const allProductCards = _.flatMap(usanaIds, usanaId => {
                    const productGroup = productGroups.find(group => {
                        return !!ProductUtil.findMatchedProduct(usanaId, Object.values(group.products));
                    });

                    if (productGroup) {
                        //findMatchedProduct uses the fuzzySearch as a fallback, so use the usanaId of the found product
                        const matchedProduct = ProductUtil.findMatchedProduct(usanaId, Object.values(productGroup.products));
                        return matchedProduct ? [{
                            usanaId: matchedProduct.usanaId,
                            productGroup: productGroup
                        }] : [];
                    }
                    return [];
                });

                this.busy = false;
                //only show first 6 products
                return _.take(_.uniqBy(allProductCards, 'productGroup.id'), 6);
            })
        );
    }

    skinAnalyzerChangeAnswers() {
        this.log.debug("Skin Analyzer change answers");
        this.subscriptions.add(this.skinAnalyzerService.revieveScriptReady.subscribe((scriptReady) => {
            if(scriptReady) {
                //I did not find a go-to page in the docs, so this is the best we got
                window['Revieve'].API.navigateBack();//1
                window['Revieve'].API.navigateBack();//2
                window['Revieve'].API.navigateBack();//3
                window['Revieve'].API.navigateBack();//4
                window['Revieve'].API.navigateBack();//5
                window['Revieve'].API.navigateBack();//6
                window['Revieve'].API.navigateBack();//7
                window['Revieve'].API.navigateBack();//8
                window['Revieve'].API.navigateBack();//9
                window['Revieve'].API.show();
            }
        }));
    }

    openCreateSharelist() {
        const shareListId = sessionStorage.getItem(Constants.SKIN_ANALYZER_SHARE_LIST_ID);
        if (shareListId) {
            this.gqlShareListService.getShareListById(shareListId).subscribe({
                next: (shareList: IShareList) => {
                    const initialState = {shareList: shareList};
                    this.modalService.show(CreateShareListModalComponent, {
                        class: `dsm-modal modal-md`,
                        initialState
                    });
                }, error: () => {
                    const initialState = {
                        titleTag: 'cart.checkout.modal.error',
                        generalErrorsInterpolated: [{tag: 'cart.create.sharelist.modal.get.error'}],
                    };
                    this.modalService.show(GeneralErrorPopupComponent, {initialState});
                }
            })
        }
    }

    addAllAndGoToCart() {
        this.log.debug("Add all products to cart and go to cart");
        this.busy = true;

        const products: IProduct[] = [];
        _.forEach(this.productCards, (card) => {
            if (card.productGroup) {
                _.forEach(card.productGroup.products, (product) => {
                    products.push(product);
                });
            }
        });

        this.log.debug("Add products:", products);

        //if we don't wait for each adjustment to finish, the cart gets messed up
        //at some point we should fix this, so we can add all products to cart at once
        from(products).pipe(
            concatMap((product: IProduct) =>
                this.cartService.adjustCartQuantity(product, 1, undefined)
            )
        ).subscribe({
            complete: () => {
                this.router.navigate([Constants.CART_OVERVIEW_STATE]);
            },
            error: (error) => {
                this.log.error("Error adding product to cart", error);
                this.busy = false;
            }
        });
    }
}
